// Copyright © 2025 The Things Industries B.V.

import React from 'react'
import { useSelector } from 'react-redux'
import { defineMessages, FormattedNumber } from 'react-intl'
import classNames from 'classnames'

import Icon, { IconApplication, IconDevice, IconGateway } from '@ttn-lw/components/icon'
import Link from '@ttn-lw/components/link'

import Message from '@ttn-lw/lib/components/message'

import Pill from '@console/components/pill/index.tti'

import sharedMessages from '@ttn-lw/lib/shared-messages'

import { selectLabels, selectLabelsTotalCount } from '@console/store/selectors/labels.tti'

import style from './header.styl'

const m = defineMessages({
  description: 'Showing last {numLabels} of {totalLabels} labels • <Link>View all</Link>',
  noLabels: 'No labels',
  noLabelsDescription: 'You don’t have any labels currently',
})

const LabelsDropdown = () => {
  const dropdownItems = useSelector(selectLabels)
  const totalLabels = useSelector(selectLabelsTotalCount)

  return (
    <>
      <div className={style.notificationsDropdownHeader}>
        <Message content={sharedMessages.labels} />{' '}
        <Message className="c-text-neutral-semilight fw-normal fs-m" content={`(${totalLabels})`} />
      </div>
      {dropdownItems && dropdownItems.length === 0 ? (
        <div className={style.emptyState}>
          <Message
            content={m.noLabels}
            className="d-block text-center fw-bold c-text-neutral-semilight"
          />
          <Message
            content={m.noLabelsDescription}
            className="d-block text-center fs-s c-text-neutral-light"
          />
        </div>
      ) : (
        <>
          {dropdownItems.slice(0, 3).map(label => (
            <Link
              to={`/labels/${label.ids.label_id}`}
              key={label.ids.label_id}
              className={classNames(
                style.labelsRow,
                'd-flex al-center j-between p-vert-cs-s p-sides-cs-l',
              )}
            >
              <Pill color={label.color} text={label.name} />
              <div className="d-flex j-center gap-cs-m c-text-neutral-semilight">
                <div className="d-flex al-center gap-cs-xxs">
                  <Icon icon={IconApplication} />
                  <FormattedNumber value={label.entities?.applications?.length || 0} />
                </div>
                <div className="d-flex al-center gap-cs-xxs">
                  <Icon icon={IconGateway} />
                  <FormattedNumber value={label.entities?.gateways?.length || 0} />
                </div>
                <div className="d-flex al-center gap-cs-xxs">
                  <Icon icon={IconDevice} />
                  <FormattedNumber value={label.entities?.end_devices?.length || 0} />
                </div>
              </div>
            </Link>
          ))}
        </>
      )}
      <div
        className={classNames(
          'p-cs-m c-text-neutral-semilight fs-s text-center c-bg-neutral-light',
          style.totalMessage,
        )}
      >
        <Message
          content={m.description}
          values={{
            numLabels: dropdownItems.slice(0, 3).length,
            totalLabels,
            Link: txt => (
              <Link to="/labels" primary className="td-none">
                {txt}
              </Link>
            ),
          }}
        />
      </div>
    </>
  )
}

export default LabelsDropdown
